/*
 * Layer z-order diagnostics, backing the Layer Order sidebar panel.
 *
 * The stack is read from map.layers, where index 0 is drawn at the BACK. Rows are returned
 * bottom-first to match that indexing; the panel reverses them for display.
 *
 * Zones (see the stack diagram in lib/layers.js):
 *   pinned  - historical imagery + contour, held below the band, contiguous from index 0
 *   band    - state layers + reference lines/labels, sorted DESCENDING by priority
 *   ui      - gohunt-* graphics and gohunt-overlay-* live layers, always on top
 *   terrain - terrain analysis raster, appended above the UI layers by MapInitializer
 *
 * Only the band participates in priority ordering, so only the band is checked for violations.
 */

import { MAP_LAYER_IDS } from './constants'

export const LAYER_ZONES = {
  band: 'band',
  pinned: 'pinned',
  terrain: 'terrain',
  ui: 'ui'
}

const TOP_TIER_IDS = new Set([
  MAP_LAYER_IDS.content,
  MAP_LAYER_IDS.sketch,
  MAP_LAYER_IDS.search,
  MAP_LAYER_IDS.select,
  MAP_LAYER_IDS.ghost
])

const PINNED_BOTTOM_IDS = new Set([MAP_LAYER_IDS.historical, MAP_LAYER_IDS.contour])

const isTopTier = layer =>
  TOP_TIER_IDS.has(layer.id) || (typeof layer.id === 'string' && layer.id.startsWith(`${MAP_LAYER_IDS.overlay}-`))

function zoneOf(layer) {
  if (PINNED_BOTTOM_IDS.has(layer.id)) return LAYER_ZONES.pinned
  if (layer.id === MAP_LAYER_IDS.terrainAnalysis) return LAYER_ZONES.terrain
  if (isTopTier(layer)) return LAYER_ZONES.ui
  return LAYER_ZONES.band
}

/* One row per layer, bottom-first (matching map.layers indexing). */
export function getLayerRows(mapRef, visibleOnly = false) {
  if (!mapRef?.layers?.items) return []

  const items = mapRef.layers.items
  const rows = []

  for (let i = 0; i < items.length; i++) {
    const layer = items[i]
    if (visibleOnly && !layer.visible) continue
    rows.push({
      id: layer.id,
      idx: i,
      opacity: layer.opacity,
      priority: layer.priority,
      type: layer.type,
      visible: layer.visible,
      zone: zoneOf(layer)
    })
  }

  return rows
}

/*
 * Adjacent pairs in the band that break the descending-priority invariant. `below` sits at
 * the lower index (drawn further back) and must have the HIGHER priority number.
 */
export function findOrderViolations(rows) {
  const band = rows.filter(row => row.zone === LAYER_ZONES.band)
  const violations = []

  for (let i = 1; i < band.length; i++) {
    const below = band[i - 1]
    const above = band[i]
    if (below.priority < above.priority) violations.push({ above, below })
  }

  return violations
}

/* Plain-text dump — used by the panel's copy button and by the console helper. */
export function formatLayerRows(rows, violations) {
  const lines = ['idx  zone     priority  visible  id', '---  -------  --------  -------  --']

  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i]
    lines.push(
      [
        String(row.idx).padStart(3),
        String(row.zone).padEnd(7),
        String(row.priority == null ? '-' : row.priority).padStart(8),
        String(row.visible).padEnd(7),
        row.id
      ].join('  ')
    )
  }

  lines.push('')
  if (!violations.length) {
    lines.push(`OK - band ordered correctly (${rows.filter(r => r.zone === LAYER_ZONES.band).length} layers)`)
  } else {
    lines.push(`FAIL - ${violations.length} ordering violation(s)`)
    for (let i = 0; i < violations.length; i++) {
      const { above, below } = violations[i]
      lines.push(`  idx ${below.idx} "${below.id}" (p=${below.priority}) is BELOW idx ${above.idx} "${above.id}" (p=${above.priority})`)
    }
  }

  return lines.join('\n')
}
